import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { LocalAiLeadCostBreakdownResponse } from "@/api/local-ai-lead-api"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

type CostRow = NonNullable<LocalAiLeadCostBreakdownResponse["siteUserCosts"]>[number]

export function SiteMonthlyCostTable({
  rows,
  costMonth,
  currency,
}: {
  rows: CostRow[]
  costMonth: string
  currency: string
}) {
  const { t } = useTranslation()
  const [descending, setDescending] = useState(true)
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) =>
        descending ? b.fullMonthly - a.fullMonthly : a.fullMonthly - b.fullMonthly
      ),
    [rows, descending]
  )
  const money = (amount: number) =>
    new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount)

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("localAiLeadPage.siteMonthlyUsers", "Cost by user · {{month}}", { month: costMonth })}
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          {t(
            "localAiLeadPage.siteMonthlyUsersDescription",
            "Monthly usage and allocated shared cost for this company."
          )}
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("localAiLeadPage.noMonthlyUsers", "No active users this month.")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("localAiLeadPage.user", "User")}</TableHead>
                  <TableHead className="text-right">
                    {t("localAiLeadPage.usageMonthly", "Usage / month")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("localAiLeadPage.sharedMonthly", "Shared / month")}
                  </TableHead>
                  <TableHead className="text-right">
                    <button
                      aria-label={t("localAiLeadPage.sortFullCost", "Sort by full monthly cost")}
                      type="button"
                      onClick={() => setDescending((value) => !value)}
                    >
                      {t("localAiLeadPage.fullMonthly", "Full / month")} {descending ? "↓" : "↑"}
                    </button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.userId}>
                    <TableCell>{row.email || row.userId}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(row.usageMonthly)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(row.sharedMonthly)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {money(row.fullMonthly)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
